import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { Theme } from '@mui/material';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';

import { colors } from '../../../utils/theme';
import { PracticeFeatureFlagKey, useFeatureFlag } from '../../../utils/user';

type Props = {
  title?: string;
  text?: string | null;
  lineText?: string | null;
  isLeftAligned?: boolean;
};

const useStyles = makeStyles((theme: Theme) => ({
  popover: {
    pointerEvents: 'none',
  },
  popoverPaper: {
    padding: theme.spacing(1),
    boxShadow: 'none',
    backgroundColor: colors.PRIMARY,
    width: 174,
  },
  fdaStatusIcon: {
    fontSize: '10px',
    display: 'flex',
  },
  lineText: {
    fontSize: 10,
  },
  iconSvg: {
    fontSize: '10px',
    verticalAlign: 'text-bottom',
    marginLeft: '4px',
    marginBottom: '1px',
  },
  popoverText: {
    fontSize: '10px',
    color: colors.PURE_WHITE,
    display: 'block',
    fontFamily: 'proxima-nova, sans-serif',
    marginBottom: '5px',
  },
}));

export const VideaInfoElement = (props: Props) => {
  const { title, text, lineText, isLeftAligned } = props;
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const isSalesDemo = !!useFeatureFlag(PracticeFeatureFlagKey.IS_SALES_DEMO)
    ?.value;

  const handlePopoverOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);
  const shouldDisplayInvestigationalDeviceMessaging = !!text;
  const isInvestigationalDevice = !!lineText;

  return (
    !isSalesDemo && (
      <Box>
        <Box
          className={classes.fdaStatusIcon}
          onMouseEnter={handlePopoverOpen}
          onMouseLeave={handlePopoverClose}
        >
          {isInvestigationalDevice ? (
            <Typography className={classes.lineText}>
              {title} ({lineText}
              <InfoOutlinedIcon className={classes.iconSvg} />)
            </Typography>
          ) : (
            <>
              {title && (
                <Typography className={classes.lineText}>{title}</Typography>
              )}
              {shouldDisplayInvestigationalDeviceMessaging && (
                <Typography className={classes.lineText}>
                  <InfoOutlinedIcon className={classes.iconSvg} />
                </Typography>
              )}
            </>
          )}
        </Box>

        {shouldDisplayInvestigationalDeviceMessaging && (
          <Popover
            id="mouse-over-popover"
            className={classes.popover}
            classes={{
              paper: classes.popoverPaper,
            }}
            open={isOpen}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: isLeftAligned ? 'left' : 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            onClose={handlePopoverClose}
            disableRestoreFocus
          >
            {lineText && (
              <Typography variant="caption" className={classes.popoverText}>
                {lineText}
              </Typography>
            )}
            <Typography variant="caption" className={classes.popoverText}>
              {text}
            </Typography>
          </Popover>
        )}
      </Box>
    )
  );
};
